import React from 'react';

export default function HRCopilotOnboardingStatus() {
  return (
    <div className="hrcopilot-status-section">
      <style>{`
/* =====================================================
   MAIN WRAPPER WITH BACKGROUND IMAGES
===================================================== */
.hrcopilot-status-section {
  max-width: 1400px;
  margin: 0 auto;
  padding: 100px 20px;
  position: relative;
  overflow: hidden;
  background: #fafafa;
}

/* Animated background layer */
.hrcopilot-status-section::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: 
    url("https://avantage.bold-themes.com/hr/wp-content/uploads/sites/5/2019/04/bgn-map.png"),
    url("https://avantage.bold-themes.com/hr/wp-content/uploads/sites/5/2019/04/bgn-people.png");
  background-repeat: no-repeat;
  background-size: 220px, 320px;
  background-position: left -40px top 180px, right -100px bottom 60px;
  opacity: 0.4;
  z-index: 0;
  animation: bgFloat 20s ease-in-out infinite alternate;
}

/* Background floating animation */
@keyframes bgFloat {
  0% { 
    background-position: left -40px top 180px, right -100px bottom 60px;
  }
  100% { 
    background-position: left 0px top 160px, right -80px bottom 80px;
  }
}

/* =====================================================
   TITLE AREA
===================================================== */
.hrcopilot-status-header {
  text-align: center;
  margin-bottom: 70px;
  position: relative;
  z-index: 1;
  animation: fadeInUp 0.8s ease-out forwards;
  opacity: 0;
}

@keyframes fadeInUp {
  from { 
    opacity: 0; 
    transform: translateY(30px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
}

.hrcopilot-status-title {
  font-size: 48px;
  font-weight: 800;
  margin-bottom: 24px;
  color: #1a1a1a;
  line-height: 1.3;
  font-family: "Raleway", sans-serif;
}

/* Highlight text with animated underline */
.hrcopilot-status-highlight {
  color: #c62828;
  font-weight: 900;
  position: relative;
  display: inline-block;
}

.hrcopilot-status-highlight::after {
  content: "";
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 0;
  height: 3px;
  background: #c62828;
  animation: underlineGrow 1.2s ease-out 0.5s forwards;
}

@keyframes underlineGrow {
  to { width: 100%; }
}

/* Description text */
.hrcopilot-status-description {
  font-size: 18px;
  color: #555;
  line-height: 1.8;
  max-width: 900px;
  margin: 0 auto;
  animation: fadeInUp 0.8s ease-out 0.3s forwards;
  opacity: 0;
}

.hrcopilot-status-bold {
  font-weight: 700;
  color: #1a1a1a;
}

/* =====================================================
   VIDEO SHOWCASE WITH ENHANCED ANIMATIONS
===================================================== */
.hrcopilot-pipeline-showcase {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 60px;
  position: relative;
  z-index: 2;
  animation: fadeInUp 0.8s ease-out 0.6s forwards;
  opacity: 0;
}

/* Video container with gradient border effect */
.hrcopilot-video-container {
  position: relative;
  width: 100%;
  max-width: 1100px;
  border-radius: 16px;
  padding: 4px;
  background: linear-gradient(135deg, #c62828, #e53935, #c62828);
  background-size: 200% 200%;
  animation: gradientShift 4s ease infinite;
  box-shadow: 0 15px 45px rgba(198, 40, 40, 0.25);
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* Video wrapper for inner border radius */
.hrcopilot-video-wrapper {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
}

.hrcopilot-pipeline-video {
  width: 100%;
  display: block;
  border-radius: 12px;
  transition: transform 0.4s ease;
}

/* Hover effect with scale and lift */
.hrcopilot-video-container:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 60px rgba(198, 40, 40, 0.35);
  transition: all 0.4s ease;
}

.hrcopilot-video-container:hover .hrcopilot-pipeline-video {
  transform: scale(1.02);
}

/* Shine overlay effect */
.hrcopilot-video-wrapper::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg, 
    transparent, 
    rgba(255, 255, 255, 0.3), 
    transparent
  );
  animation: shine 3s ease-in-out infinite;
  pointer-events: none;
}

@keyframes shine {
  0% { left: -100%; }
  20% { left: 100%; }
  100% { left: 100%; }
}

/* Play button overlay (appears on hover) */
.hrcopilot-play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: rgba(198, 40, 40, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 3;
}

.hrcopilot-video-container:hover .hrcopilot-play-overlay {
  opacity: 1;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50% { transform: translate(-50%, -50%) scale(1.1); }
}

.hrcopilot-play-overlay::before {
  content: "";
  width: 0;
  height: 0;
  border-left: 20px solid white;
  border-top: 12px solid transparent;
  border-bottom: 12px solid transparent;
  margin-left: 4px;
}

/* Corner decorative elements */
.hrcopilot-corner-decoration {
  position: absolute;
  width: 40px;
  height: 40px;
  border: 3px solid #c62828;
  opacity: 0;
  animation: cornerFade 0.6s ease-out 1s forwards;
}

@keyframes cornerFade {
  to { opacity: 0.6; }
}

.hrcopilot-corner-tl {
  top: -2px;
  left: -2px;
  border-right: none;
  border-bottom: none;
  border-radius: 16px 0 0 0;
}

.hrcopilot-corner-br {
  bottom: -2px;
  right: -2px;
  border-left: none;
  border-top: none;
  border-radius: 0 0 16px 0;
}

/* =====================================================
   FLOATING PARTICLES DECORATION
===================================================== */
.hrcopilot-particle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: #c62828;
  border-radius: 50%;
  opacity: 0.3;
  z-index: 0;
}

.hrcopilot-particle-1 {
  top: 10%;
  left: 5%;
  animation: float 4s ease-in-out infinite;
}

.hrcopilot-particle-2 {
  top: 30%;
  right: 8%;
  animation: float 5s ease-in-out infinite 1s;
}

.hrcopilot-particle-3 {
  bottom: 15%;
  left: 10%;
  animation: float 6s ease-in-out infinite 2s;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
}

/* =====================================================
   RESPONSIVE STYLES
===================================================== */
@media (max-width: 1024px) {
  .hrcopilot-status-title { font-size: 40px; }
  .hrcopilot-status-description { font-size: 17px; }
}

@media (max-width: 768px) {
  .hrcopilot-status-section { padding: 70px 16px; }
  .hrcopilot-status-title { font-size: 32px; }
  .hrcopilot-status-description { font-size: 16px; }
  
  .hrcopilot-video-container {
    padding: 3px;
  }
  
  .hrcopilot-play-overlay {
    width: 60px;
    height: 60px;
  }
  
  .hrcopilot-play-overlay::before {
    border-left-width: 15px;
    border-top-width: 9px;
    border-bottom-width: 9px;
  }
}

@media (max-width: 480px) {
  .hrcopilot-status-title { font-size: 26px; }
  .hrcopilot-status-description { font-size: 15px; }
}
      `}</style>

      {/* Floating particles decoration */}
      <div className="hrcopilot-particle hrcopilot-particle-1"></div>
      <div className="hrcopilot-particle hrcopilot-particle-2"></div>
      <div className="hrcopilot-particle hrcopilot-particle-3"></div>

      {/* HEADER */}
      <div className="hrcopilot-status-header">
        <h2 className="hrcopilot-status-title">
          Never wonder about a job's <span className="hrcopilot-status-highlight">status</span>
        </h2>

        <p className="hrcopilot-status-description">
          <span className="hrcopilot-status-bold">All job positions, all in one place.</span> {' '}
          Stay updated on every job posting, applicant, and scheduled activity with a real-time Recruitment dashboard.
        </p>
      </div>

      {/* VIDEO SHOWCASE WITH ENHANCED WRAPPER */}
      <div className="hrcopilot-pipeline-showcase">
        <div className="hrcopilot-video-container">
          {/* Corner decorations */}
          <div className="hrcopilot-corner-decoration hrcopilot-corner-tl"></div>
          <div className="hrcopilot-corner-decoration hrcopilot-corner-br"></div>
          
          <div className="hrcopilot-video-wrapper">
            {/* Play button overlay */}
            <div className="hrcopilot-play-overlay"></div>
            
            <video 
              className="hrcopilot-pipeline-video"
              autoPlay 
              loop 
              muted 
              playsInline
            >
              <source src="/images/vid2.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </div>
    </div>
  );
}